/**
 * ProjectShowcase — V3 Day 2 (2026-05-08)
 *
 * Cinematic full-screen modal that walks the prospect through 24 slides
 * of the KEY2 tower (7 building shots → "Lifestyle" chapter card → 9
 * lifestyle hero shots → "Details" chapter card → 6 detail shots).
 *
 * Behaviours:
 *   - Auto-advance every 5s (chapter cards 4s); pauses on hover/focus.
 *   - Ken Burns: each image starts scaled 1.05 and eases to 1.00 over
 *     the slide's lifetime — subtle motion, never busy.
 *   - Touch swipe via framer-motion drag on the viewer area.
 *   - Keyboard: ESC close, Space play/pause, ←/→ nav, F fullscreen.
 *   - RTL: dir attribute on the root + flex direction follows the lang.
 *     We do NOT swap arrow keys in RTL — keyboard arrow direction is
 *     physical, not directional, on macOS / iOS / Windows.
 *   - Preloads next 2 slides only (cheap; respects mobile data).
 *   - Fallback: if an image fails to load, render a gold gradient
 *     panel showing the slide title.
 *   - Audio: state-only hook for now (Mefteh will provide a music file
 *     later); the toggle button does nothing audible yet.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AnimatePresence,
  motion,
  type PanInfo,
} from 'framer-motion';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  Maximize,
  Volume2,
  VolumeX,
  AlertTriangle,
} from 'lucide-react';
import { SHOWCASE_SLIDES, type ShowcaseSlide } from '@data/showcase-order';
import { useT } from '@lib/i18n';
import { useSessionStore } from '@stores/sessionStore';

interface ProjectShowcaseProps {
  open: boolean;
  onClose: () => void;
}

const IMAGE_DURATION_MS = 5000;
const CHAPTER_DURATION_MS = 4000;
const SWIPE_THRESHOLD_PX = 60;

// Bumping this breaks any browser-cached image URL (panoramas had a
// pre-Worker no-ACAO entry on disk; keep this trick consistent across
// VR + Showcase so a future CORS-affecting deploy doesn't get held
// up by stale local cache.)
const SHOWCASE_CACHE_VERSION = 'v2-20260508';

function withVersion(url: string): string {
  return url.includes('?')
    ? `${url}&v=${SHOWCASE_CACHE_VERSION}`
    : `${url}?v=${SHOWCASE_CACHE_VERSION}`;
}

export default function ProjectShowcase({
  open,
  onClose,
}: ProjectShowcaseProps) {
  const t = useT();
  const lang = useSessionStore((s) => s.language);
  const dir: 'ltr' | 'rtl' = lang === 'ar' ? 'rtl' : 'ltr';

  const slides = SHOWCASE_SLIDES;
  const total = slides.length;

  const [index, setIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isPausedByHover, setIsPausedByHover] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [imageError, setImageError] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);

  // ─── Reset state on open ────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setIndex(0);
    setIsPlaying(true);
    setImageError(false);
  }, [open]);

  // ─── Body scroll lock ───────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // ─── Navigation helpers ────────────────────────────────────────
  const goNext = useCallback(() => {
    setImageError(false);
    setIndex((i) => (i + 1) % total);
  }, [total]);

  const goPrev = useCallback(() => {
    setImageError(false);
    setIndex((i) => (i - 1 + total) % total);
  }, [total]);

  const togglePlay = useCallback(() => {
    setIsPlaying((p) => !p);
  }, []);

  const toggleAudio = useCallback(() => {
    setAudioEnabled((a) => !a);
    // Future: toggle <audio>.muted here when Mefteh provides a track.
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        void document.exitFullscreen();
      } else {
        void el.requestFullscreen();
      }
    } catch {
      /* some browsers reject without a user gesture; ignore */
    }
  }, []);

  // ─── Keyboard ───────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case ' ':
        case 'Spacebar':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowRight':
          goNext();
          break;
        case 'ArrowLeft':
          goPrev();
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, goNext, goPrev, togglePlay, toggleFullscreen]);

  // ─── Auto-advance ───────────────────────────────────────────────
  const currentSlide: ShowcaseSlide | undefined = slides[index];
  const slideDuration =
    currentSlide?.kind === 'chapter' ? CHAPTER_DURATION_MS : IMAGE_DURATION_MS;

  useEffect(() => {
    if (!open) return;
    if (!isPlaying || isPausedByHover) return;
    const id = window.setTimeout(goNext, slideDuration);
    return () => window.clearTimeout(id);
  }, [open, isPlaying, isPausedByHover, index, goNext, slideDuration]);

  // ─── Preload next 2 ─────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    for (let offset = 1; offset <= 2; offset++) {
      const peek = slides[(index + offset) % total];
      if (peek?.kind === 'image' && peek.url) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = withVersion(peek.url);
      }
    }
  }, [open, index, slides, total]);

  // ─── Drag → swipe ───────────────────────────────────────────────
  const handleDragEnd = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo
  ) => {
    if (info.offset.x < -SWIPE_THRESHOLD_PX) {
      // physical swipe-left → next (works the same way in LTR + RTL,
      // matches what mobile users expect from a photo gallery)
      goNext();
    } else if (info.offset.x > SWIPE_THRESHOLD_PX) {
      goPrev();
    }
  };

  // ─── Captions ───────────────────────────────────────────────────
  const captionTitle = useMemo(() => {
    if (!currentSlide) return '';
    return lang === 'ar' ? currentSlide.titleAr : currentSlide.titleEn;
  }, [currentSlide, lang]);

  const captionSubtitle = useMemo(() => {
    if (!currentSlide) return '';
    const v = lang === 'ar' ? currentSlide.subtitleAr : currentSlide.subtitleEn;
    return v ?? '';
  }, [currentSlide, lang]);

  const counterText = `${index + 1} / ${total}`;
  const progressPct = ((index + 1) / total) * 100;

  if (!open || !currentSlide) return null;

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label={t('showcase.title')}
      dir={dir}
      onMouseEnter={() => setIsPausedByHover(true)}
      onMouseLeave={() => setIsPausedByHover(false)}
      className="fixed inset-0 z-[9999] bg-black flex flex-col"
    >
      {/* ── Top bar ──────────────────────────────────────────── */}
      <div className="absolute top-0 inset-x-0 z-20 flex items-start justify-between gap-3 px-4 py-3 bg-gradient-to-b from-black/80 via-black/40 to-transparent text-white">
        <div className="min-w-0 flex-1">
          <p className="text-base md:text-lg font-bold truncate">
            {captionTitle}
          </p>
          {captionSubtitle && (
            <p className="text-xs md:text-sm text-white/75 truncate">
              {captionSubtitle}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('showcase.close')}
          title={t('showcase.close')}
          className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-red-500/70 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* ── Viewer (drag area) ───────────────────────────────── */}
      <motion.div
        className="absolute inset-0 select-none"
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.18}
        dragMomentum={false}
        onDragEnd={handleDragEnd}
      >
        <AnimatePresence mode="wait">
          {currentSlide.kind === 'image' ? (
            <SlideImage
              key={currentSlide.id}
              slide={currentSlide}
              hasError={imageError}
              onError={() => setImageError(true)}
              onLoad={() => setImageError(false)}
              durationMs={slideDuration}
              fallbackTitle={captionTitle}
              fallbackErrorLabel={t('showcase.imageError')}
            />
          ) : (
            <SlideChapter key={currentSlide.id} slide={currentSlide} lang={lang} />
          )}
        </AnimatePresence>
      </motion.div>

      {/* ── Bottom bar ───────────────────────────────────────── */}
      <div className="absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/85 via-black/55 to-transparent text-white">
        {/* Progress bar (gold) */}
        <div className="h-[5px] bg-white/10 overflow-hidden">
          <div
            className="h-full bg-gold transition-[width] duration-300 ease-out"
            style={{ width: `${progressPct}%` }}
            aria-hidden
          />
        </div>

        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <div className="flex items-center gap-2">
            <CtrlButton
              onClick={goPrev}
              label={t('showcase.previous')}
              dir={dir}
              variant="prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </CtrlButton>
            <CtrlButton
              onClick={togglePlay}
              label={isPlaying ? t('showcase.pause') : t('showcase.play')}
              accent="primary"
            >
              {isPlaying ? (
                <Pause className="w-5 h-5" />
              ) : (
                <Play className="w-5 h-5 ms-0.5" />
              )}
            </CtrlButton>
            <CtrlButton
              onClick={goNext}
              label={t('showcase.next')}
              dir={dir}
              variant="next"
            >
              <ChevronRight className="w-5 h-5" />
            </CtrlButton>
          </div>

          <span className="text-sm font-medium num-en text-white/85 tabular-nums">
            {counterText}
          </span>

          <div className="flex items-center gap-2">
            <CtrlButton
              onClick={toggleAudio}
              label={audioEnabled ? t('showcase.audioOn') : t('showcase.audioOff')}
              active={audioEnabled}
            >
              {audioEnabled ? (
                <Volume2 className="w-5 h-5" />
              ) : (
                <VolumeX className="w-5 h-5" />
              )}
            </CtrlButton>
            <CtrlButton onClick={toggleFullscreen} label={t('showcase.fullscreen')}>
              <Maximize className="w-5 h-5" />
            </CtrlButton>
          </div>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Slide variants
// ──────────────────────────────────────────────────────────────────

function SlideImage({
  slide,
  hasError,
  onError,
  onLoad,
  durationMs,
  fallbackTitle,
  fallbackErrorLabel,
}: {
  slide: Extract<ShowcaseSlide, { kind: 'image' }>;
  hasError: boolean;
  onError: () => void;
  onLoad: () => void;
  durationMs: number;
  fallbackTitle: string;
  fallbackErrorLabel: string;
}) {
  // Ken Burns: subtle 1.05→1.00 across the slide's lifetime.
  // Slightly longer than auto-advance so the motion never "lands" on a
  // hard stop right when the next slide kicks in.
  const kenBurnsSec = (durationMs + 600) / 1000;

  if (hasError) {
    return (
      <motion.div
        key={`${slide.id}-fallback`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        className="absolute inset-0 flex flex-col items-center justify-center text-center px-6
                   bg-gradient-to-br from-gold via-gold-300 to-navy"
      >
        <AlertTriangle className="w-10 h-10 text-navy mb-3" />
        <p className="text-2xl md:text-3xl font-bold text-navy">
          {fallbackTitle}
        </p>
        <p className="text-sm text-navy/70 mt-2">{fallbackErrorLabel}</p>
      </motion.div>
    );
  }

  return (
    <motion.img
      key={slide.id}
      src={withVersion(slide.url)}
      alt={slide.titleEn}
      crossOrigin="anonymous"
      onError={onError}
      onLoad={onLoad}
      initial={{ opacity: 0, scale: 1.05 }}
      animate={{ opacity: 1, scale: 1.0 }}
      exit={{ opacity: 0 }}
      transition={{
        opacity: { duration: 0.6, ease: 'easeOut' },
        scale: { duration: kenBurnsSec, ease: 'easeOut' },
      }}
      className="absolute inset-0 w-full h-full object-contain pointer-events-none"
      draggable={false}
    />
  );
}

function SlideChapter({
  slide,
  lang,
}: {
  slide: Extract<ShowcaseSlide, { kind: 'chapter' }>;
  lang: 'ar' | 'en';
}) {
  const title = lang === 'ar' ? slide.titleAr : slide.titleEn;
  const subtitle = lang === 'ar' ? slide.subtitleAr : slide.subtitleEn;
  return (
    <motion.div
      key={slide.id}
      initial={{ opacity: 0, scale: 1.04 }}
      animate={{ opacity: 1, scale: 1.0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className="absolute inset-0 flex flex-col items-center justify-center text-center px-6
                 bg-gradient-to-br from-navy-900 via-navy to-gold/40"
    >
      <div className="w-16 h-[2px] bg-gold mb-6" aria-hidden />
      <p className="text-3xl md:text-5xl font-extrabold text-white drop-shadow-lg">
        {title}
      </p>
      {subtitle && (
        <p className="mt-4 text-sm md:text-lg text-white/80 max-w-xl">
          {subtitle}
        </p>
      )}
      <div className="w-16 h-[2px] bg-gold mt-6" aria-hidden />
    </motion.div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Reusable control button
// ──────────────────────────────────────────────────────────────────

function CtrlButton({
  onClick,
  label,
  children,
  active,
  accent,
  dir,
  variant,
}: {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
  active?: boolean;
  accent?: 'primary';
  dir?: 'ltr' | 'rtl';
  variant?: 'next' | 'prev';
}) {
  // Mirror the chevron icon in RTL for prev/next so the arrow always
  // visually points "in the direction of motion" relative to the
  // reading direction the user is in.
  const flipIcon =
    dir === 'rtl' && (variant === 'next' || variant === 'prev');
  const base =
    'inline-flex items-center justify-center w-12 h-12 rounded-full transition-colors';
  const colorCls =
    accent === 'primary'
      ? 'bg-gold text-navy hover:bg-gold-300'
      : active
        ? 'bg-gold/25 text-gold ring-1 ring-gold/40'
        : 'bg-white/10 hover:bg-white/20 text-white';
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`${base} ${colorCls}`}
    >
      <span className={flipIcon ? 'rotate-180' : ''}>{children}</span>
    </button>
  );
}
